import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, message, Image, Space, Spin } from 'antd';
import { UserOutlined, LockOutlined, ReloadOutlined } from '@ant-design/icons';
import { adminLogin, getCaptcha, clearError } from './authSlice';
import CryptoJS from 'crypto-js';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, captcha } = useSelector((state) => state.auth);
  
  const [form] = Form.useForm();
  const [adminCaptchaCode, setAdminCaptchaCode] = useState('');

  // 密码加密函数
  const encryptPassword = (password) => {
    return CryptoJS.SHA256(password).toString();
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // 组件挂载时自动加载验证码
  useEffect(() => {
    dispatch(getCaptcha());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (captcha.error) {
      message.error(captcha.error);
    }
  }, [captcha.error]);

  // 管理员登录
  const handleAdminLogin = async (values) => {
    try {
      await dispatch(adminLogin({
        username: values.username,
        password: encryptPassword(values.password),
        captchaId: captcha.captchaId,
        captchaCode: values.captchaCode
      })).unwrap();
      message.success('登录成功');
      navigate('/dashboard');
    } catch (error) {
      // 错误已在slice中处理
      // 登录失败后刷新验证码
      dispatch(getCaptcha());
      setAdminCaptchaCode('');
    }
  };

  // 获取图形验证码
  const handleGetCaptcha = async () => {
    try {
      await dispatch(getCaptcha()).unwrap();
      message.success('验证码已刷新');
      setAdminCaptchaCode('');
    } catch (error) {
      message.error(error || '获取验证码失败');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px'
        }}
        bodyStyle={{ padding: '40px 32px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: '600', 
            color: '#262626',
            marginBottom: '8px'
          }}>
            金融后台管理系统
          </h1>
          <p style={{ color: '#8c8c8c', fontSize: '14px' }}>
            管理员登录
          </p>
        </div>

        <Form
          form={form}
          name="adminLogin"
          onFinish={handleAdminLogin}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入管理员用户名' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="管理员用户名"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6位' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                  图形验证码
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Form.Item
                    name="captchaCode"
                    style={{ flex: 1, margin: 0 }}
                    rules={[
                      { required: true, message: '请输入验证码' },
                      { len: 4, message: '验证码为4位数字' }
                    ]}
                  >
                    <Input
                      placeholder="请输入图形验证码"
                      value={adminCaptchaCode}
                      onChange={(e) => setAdminCaptchaCode(e.target.value.toUpperCase())}
                      maxLength={4}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Item>
                  <Button
                    loading={captcha.isLoading}
                    onClick={handleGetCaptcha}
                    icon={<ReloadOutlined />}
                    style={{ borderRadius: '8px' }}
                  >
                    刷新
                  </Button>
                </div>
              </div>
              {captcha.captchaImage && (
                <div style={{ textAlign: 'center', border: '1px solid #ddd', borderRadius: '8px', padding: '8px' }}>
                  <Spin spinning={captcha.isLoading}>
                    <Image
                      src={captcha.captchaImage}
                      alt="图形验证码"
                      style={{ maxWidth: '100%', height: 'auto', cursor: 'pointer' }}
                      preview={false}
                      onClick={handleGetCaptcha}
                    />
                  </Spin>
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#999' }}>
                    点击图片可以刷新
                  </p>
                </div>
              )}
            </Space>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              block
              style={{
                height: '48px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              登录
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '24px', color: '#8c8c8c', fontSize: '12px' }}>
          <p>© 2024 金融后台管理系统. All rights reserved.</p>
        </div>
      </Card>
    </div>
  );
};

export default Login;
